import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Search, Plus, Edit, Trash2, Box, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, Check, PauseCircle, Package, Barcode, TrendingUp, Activity, Tag, MapPin, User } from 'lucide-react';
import InventaireListPageShell from './components/InventaireListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { InventaireModalPortal, InventaireViewModalPortal } from './components/inventaireModalPortal';
import FacturationFormModalTabs from '../facturation/components/FacturationFormModalTabs';
import {
  sliceQawaemStats,
  buildInventaireBulkDeleteFilterAction,
  InventaireTableSelectAllHeader,
  InventaireTableRowCheckbox,
  INVENTAIRE_TABLE_ACTIONS_TH_CLASS,
  buildInventaireImportExportProps,
  buildInventaireColumnVisibilityState,
  buildInventaireQawaemColumnDefs,
  isInventaireColumnVisible,
  inventaireTableColSpan,
  InventoryConfigNotice,
  useInventaireListSettings,
  sliceInventaireListItems,
  inventaireRecordInDateRange,
  INVENTAIRE_FORM_GRID,
  fetchInventaireWarehouses,
  buildInventaireWarehouseSelectOptions,
  mapInventaireWarehouseSelection,
} from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { useTranslation } from 'react-i18next';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';

const PickingPreparationPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.pickingPreparationPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const formatDate = (value) => (value ? new Date(value).toLocaleDateString(dateLocale) : null);
  const inventoryConfig = useInventoryConfig();
  const { dateRange, viewMode, setViewMode, itemsPerPage } = useInventaireListSettings(inventoryConfig);
  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Status dropdown
      const isClickOnStatusButton = event.target.closest('[data-picking-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-picking-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [pickings, setPickings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  // Collapsible sections state
  const [pickingFormTab, setPickingFormTab] = useState('basicInfo');

  const pickingFormTabs = useMemo(() => ([
    { id: 'basicInfo', label: p('section_identification'), icon: ShoppingCart, iconColor: 'text-emerald-400' },
    { id: 'assignmentInfo', label: p('section_assignment_form'), icon: User, iconColor: 'text-purple-400' },
  ]), [p]);

  
  const [formData, setFormData] = useState({ 
    order_id: '', 
    order_number: '',
    customer_name: '',
    priority: 'normal',
    picking_date: new Date().toISOString().split('T')[0],
    due_date: null,
    assigned_to: '',
    picker_name: '',
    warehouse_id: '',
    zone_id: '',
    status: 'pending',
    total_items: '',
    picked_items: '',
    progress_percentage: 0,
    picking_method: 'order',
    verification_required: false,
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_pickings');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Orders state
  const [availableOrders, setAvailableOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
  // Warehouses state
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  // Multi-column sorting state

  useEffect(() => { 
    loadPickings();
    loadAvailableOrders();
    loadWarehouses();
  }, []);
  
  const loadWarehouses = async () => {
    try {
      setLoadingWarehouses(true);
      setAvailableWarehouses(await fetchInventaireWarehouses());
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setAvailableWarehouses([]);
    } finally {
      setLoadingWarehouses(false);
    }
  };
  
  const loadAvailableOrders = async () => {
    try {
      setLoadingOrders(true);
      // Fetch all orders (pending, confirmed, processing) that can be picked
      const response = await localApiAdapter.get('/ventes/sales-orders');
      const orders = response?.data || response || [];
      
      // Filter to show only orders that are not cancelled or completed
      const pickableOrders = orders.filter(order => 
        !['cancelled', 'delivered', 'completed'].includes(order.status?.toLowerCase())
      );
      
      setAvailableOrders(pickableOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
      setAvailableOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };
  
  const handleOrderSelection = async (orderId) => {
    if (!orderId) {
      setSelectedOrderId(null);
      setFormData(prev => ({
        ...prev,
        order_id: '',
        order_number: '',
        customer_name: '',
        total_items: '',
        picked_items: ''
      }));
      return;
    }
    
    try {
      setSelectedOrderId(orderId);
      
      // Fetch full order details
      const response = await localApiAdapter.get(`/ventes/sales-orders/${orderId}`);
      const order = response?.data || response;
      
      if (order) {
        // For now, use a default value of 1 item until we have order items table
        // TODO: Fetch from ventes_sales_order_items table when available
        const totalItems = order.items?.length || 1;
        
        setFormData(prev => ({
          ...prev,
          order_id: order.id?.toString() || '',
          order_number: order.order_number || '',
          customer_name: order.customer_name || '',
          total_items: totalItems.toString(),
          picked_items: ''
        }));
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      alert(p('alert_order_load_error'));
    }
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) setShowStatusDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPickings = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/pickings');
      setPickings(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading pickings:', error);
      setPickings([]);
    }
  };

  const stats = {
    total: pickings.length,
    pending: pickings.filter(p => p.status === 'pending').length,
    in_progress: pickings.filter(p => p.status === 'in_progress').length,
    completed: pickings.filter(p => p.status === 'completed').length,
    urgent: pickings.filter(p => p.priority === 'urgent' && p.status !== 'completed').length,
    averageProgress: pickings.length > 0 ? (pickings.reduce((sum, p) => sum + (parseFloat(p.progress_percentage) || 0), 0) / pickings.length).toFixed(0) : 0
  };

  const mainTabs = [
    { id: 'all_pickings', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_priorite', name: p('tab_by_priority'), icon: AlertCircle },
    { id: 'par_assignation', name: p('tab_by_assignment'), icon: User }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'pending', name: p('status_pending'), icon: Clock },
    { id: 'in_progress', name: p('status_in_progress'), icon: Activity },
    { id: 'completed', name: p('status_completed'), icon: CheckCircle }
  ];

  const pickingDistribution = {
    all_pickings: {
      title: p('overview_title'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
        { filterKey: 'in_progress', name: p('dist_in_progress'), count: stats.in_progress, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_priorite: {
      title: p('distribution_by_priority'),
      data: [
        { filterKey: 'urgent', name: p('priority_urgent'), count: pickings.filter(pick => pick.priority === 'urgent').length, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterKey: 'high', name: p('priority_high'), count: pickings.filter(pick => pick.priority === 'high').length, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'normal', name: p('priority_normal'), count: pickings.filter(pick => pick.priority === 'normal').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'low', name: p('priority_low'), count: pickings.filter(pick => pick.priority === 'low').length, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_assignation: {
      title: p('distribution_by_assignment'),
      data: [
        { filterKey: 'assigned', name: p('dist_assigned'), count: pickings.filter(pick => pick.assigned_to).length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'unassigned', name: p('dist_unassigned'), count: pickings.filter(pick => !pick.assigned_to).length, color: "bg-gradient-to-r from-gray-500 to-gray-600" }
      ]
    }
  };

  const distributionInfo = pickingDistribution[mainViewMode] || pickingDistribution.all_pickings;

  const baseFilteredPickings = pickings.filter(pick => {
    if (!inventaireRecordInDateRange(dateRange, pick.picking_date, pick.due_date, pick.created_at)) return false;

    if (searchTerm && !pick.picking_number?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !pick.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !pick.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_pickings') {
      if (quickFilter === 'pending' && pick.status !== 'pending') return false;
      if (quickFilter === 'in_progress' && pick.status !== 'in_progress') return false;
      if (quickFilter === 'completed' && pick.status !== 'completed') return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_pickings':
          if (activeDistributionFilter === 'pending' && pick.status !== 'pending') return false;
          if (activeDistributionFilter === 'in_progress' && pick.status !== 'in_progress') return false;
          if (activeDistributionFilter === 'completed' && pick.status !== 'completed') return false;
          break;
        case 'par_priorite':
          if (activeDistributionFilter === 'urgent' && pick.priority !== 'urgent') return false;
          if (activeDistributionFilter === 'high' && pick.priority !== 'high') return false;
          if (activeDistributionFilter === 'normal' && pick.priority !== 'normal') return false;
          if (activeDistributionFilter === 'low' && pick.priority !== 'low') return false;
          break;
        case 'par_assignation':
          if (activeDistributionFilter === 'assigned' && !pick.assigned_to) return false;
          if (activeDistributionFilter === 'unassigned' && pick.assigned_to) return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('picking_number'),
    textColumnRule('customer_name'),
    exactColumnRule('priority', (r) => r.priority),
    textColumnRule('progress_percentage', (r) => String(r.progress_percentage ?? '')),
    textColumnRule('picker_name', (r) => String(r.picker_name ?? r.picker ?? '')),
    exactColumnRule('status', (r) => r.status),
  ], []);

  const filteredPickings = useMemo(
    () => applyColumnFilters(baseFilteredPickings, colFilters, columnFilterRules),
    [baseFilteredPickings, colFilters, columnFilterRules],
  );
  const visiblePickings = useMemo(
    () => sliceInventaireListItems(filteredPickings, itemsPerPage),
    [filteredPickings, itemsPerPage],
  );

  const openNewPickingModal = () => {
    if (!inventoryConfig.enableWarehouses) {
      window.alert(p('notice_warehouses_disabled', { defaultValue: 'إدارة المستودعات معطّلة من إعدادات المخزون.' }));
      return;
    }
    setEditingRecord(null);
    setPickingFormTab('basicInfo');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Calculate progress percentage
    const totalItems = parseInt(formData.total_items) || 0;
    const pickedItems = parseInt(formData.picked_items) || 0;
    const progressPercentage = totalItems > 0 ? ((pickedItems / totalItems) * 100).toFixed(0) : 0;
    
    try {
      const submitData = { 
        order_id: parseInt(formData.order_id) || null,
        order_number: formData.order_number,
        customer_name: formData.customer_name,
        priority: formData.priority,
        picking_date: formData.picking_date ? new Date(formData.picking_date).toISOString() : null,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        assigned_to: parseInt(formData.assigned_to) || null,
        picker_name: formData.picker_name,
        warehouse_id: parseInt(formData.warehouse_id) || null,
        zone_id: parseInt(formData.zone_id) || null,
        status: formData.status,
        total_items: totalItems,
        picked_items: pickedItems,
        progress_percentage: parseFloat(progressPercentage),
        picking_method: formData.picking_method,
        verification_required: formData.verification_required || false,
        notes: formData.notes || ''
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/inventory/pickings/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'التحضير' }));
      } else {
        await localApiAdapter.post('/inventory/pickings', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'التحضير' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      return;
    } finally {
      resetForm();
      loadPickings();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      order_id: record.order_id?.toString() || '',
      order_number: record.order_number || '',
      customer_name: record.customer_name || '',
      priority: record.priority || 'normal',
      picking_date: record.picking_date ? new Date(record.picking_date) : null,
      due_date: record.due_date ? new Date(record.due_date) : null,
      assigned_to: record.assigned_to?.toString() || '',
      picker_name: record.picker_name || '',
      warehouse_id: record.warehouse_id?.toString() || '',
      zone_id: record.zone_id?.toString() || '',
      status: record.status || 'pending',
      total_items: record.total_items?.toString() || '',
      picked_items: record.picked_items?.toString() || '',
      progress_percentage: record.progress_percentage || 0,
      picking_method: record.picking_method || 'order',
      verification_required: record.verification_required || false,
      notes: record.notes || ''
    });
    setPickingFormTab('basicInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/inventory/pickings/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'التحضير' }));
        loadPickings();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const filteredPickingsIds = useMemo(() => filteredPickings.map((r) => r.id), [filteredPickings]);
  const allRowsSelected = filteredPickingsIds.length > 0 && filteredPickingsIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredPickingsIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };
  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/inventory/pickings/${id}`)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label', { defaultValue: 'التحضير' }));
        setSelectedRecords([]);
        loadPickings();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      order_id: '', 
      order_number: '',
      customer_name: '',
      priority: 'normal',
      picking_date: null,
      due_date: null,
      assigned_to: '',
      picker_name: '',
      warehouse_id: '',
      zone_id: '',
      status: 'pending',
      total_items: '',
      picked_items: '',
      progress_percentage: 0,
      picking_method: 'order',
      verification_required: false,
      notes: ''
    });
    setSelectedOrderId(null);
    setEditingRecord(null);
    setPickingFormTab('basicInfo');
    setShowModal(false);
  };


  // Export handlers
  const handleExport = () => {
    exportToCSV(filteredPickings, 'picking_preparation', [
      { key: 'picking_number', label: p('export_col_picking') },
      { key: 'order_number', label: p('export_col_order') },
      { key: 'customer_name', label: p('export_col_customer') },
      { key: 'priority', label: p('export_col_priority') },
      { key: 'status', label: p('export_col_status') },
      { key: 'progress_percentage', label: p('export_col_progress') }
    ]);
  };

  // Export PDF
  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      const filteredData = filteredPickings;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('export_col_picking')}</th>
                <th>${p('report_pdf_order')}</th>
                <th>${p('export_col_customer')}</th>
                <th>${p('export_col_priority')}</th>
                <th>${p('export_col_progress')}</th>
                <th>${p('export_col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.picking_number || p('na')}</td>
                  <td>${item.order_number || p('na')}</td>
                  <td>${item.customer_name || p('na')}</td>
                  <td>${item.priority || p('na')}</td>
                  <td>${item.progress_percentage || 0}%</td>
                  <td>${item.status || p('na')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_title'), {});
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Export Excel (Advanced)
  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      
      const filteredData = filteredPickings;
      
      const columns = [
        { key: 'picking_number', label: p('export_col_picking'), width: 20 },
        { key: 'order_number', label: p('export_col_order'), width: 20 },
        { key: 'customer_name', label: p('export_col_customer'), width: 25 },
        { key: 'priority', label: p('export_col_priority'), width: 12 },
        { key: 'status', label: p('export_col_status'), width: 15 },
        { key: 'total_items', label: p('export_col_total_items'), width: 12 },
        { key: 'picked_items', label: p('export_col_picked_items'), width: 15 },
        { key: 'progress_percentage', label: p('export_col_progress_pct'), width: 15 },
        { key: 'picker_name', label: p('export_col_picker'), width: 20 },
        { key: 'due_date', label: p('export_col_due_date'), width: 15 }
      ];
      
      const data = filteredData.map(item => ({
        picking_number: item.picking_number || '',
        order_number: item.order_number || '',
        customer_name: item.customer_name || '',
        priority: item.priority || '',
        status: item.status || '',
        total_items: item.total_items || 0,
        picked_items: item.picked_items || 0,
        progress_percentage: item.progress_percentage || 0,
        picker_name: item.picker_name || '',
        due_date: item.due_date ? formatDate(item.due_date) : ''
      }));
      
      await generateReportExcel('picking_report', p('report_title'), {
        sheetName: p('report_sheet_name'),
      });
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const statusOptions = [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'in_progress', label: p('status_in_progress'), icon: Activity, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'completed', label: p('status_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/inventory/pickings/${recordId}`, { status: newStatus });
      await loadPickings();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };


  const getPriorityBadge = (priority) => {
    const priorities = {
      urgent: { label: p('priority_urgent'), color: 'bg-red-500/20 text-red-400 border-red-500/30' },
      high: { label: p('priority_high'), color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
      normal: { label: p('priority_normal'), color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      low: { label: p('priority_low'), color: 'bg-green-500/20 text-green-400 border-green-500/30' }
    };
    const config = priorities[priority] || priorities.normal;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>{config.label}</span>;
  };

  const getProgressBar = (percentage) => {
    const progress = parseFloat(percentage) || 0;
    let colorClass = 'bg-blue-500';
    if (progress === 100) colorClass = 'bg-green-500';
    else if (progress >= 50) colorClass = 'bg-blue-500';
    else colorClass = 'bg-yellow-500';
    
    return (
      <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden">
        <div className={`h-2 rounded-full ${colorClass} transition-all`} style={{ width: `${progress}%` }}></div>
      </div>
    );
  };

  const INVENTAIRE_TABLE_COLUMNS = [
    { key: 'picking_number', labelKey: 'col_picking_number' },
    { key: 'customer_name', labelKey: 'col_customer_order' },
    { key: 'priority', labelKey: 'col_priority' },
    { key: 'progress_percentage', labelKey: 'col_progress' },
    { key: 'picker_name', labelKey: 'col_picker' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildInventaireColumnVisibilityState(INVENTAIRE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildInventaireQawaemColumnDefs(INVENTAIRE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isInventaireColumnVisible(columnVisibility, key);
  const tableColSpan = inventaireTableColSpan(columnVisibility);

  const inventaireStatEntries = useMemo(() => sliceQawaemStats([
    { key: 'stat_total', label: p('stat_total'), value: stats.total, icon: Package },
    { key: 'stat_pending', label: p('stat_pending'), value: stats.pending, icon: Clock },
    { key: 'stat_in_progress', label: p('stat_in_progress'), value: stats.in_progress, icon: Activity },
    { key: 'stat_completed', label: p('stat_completed'), value: stats.completed, icon: CheckCircle },
  ], 4), [stats, p]);


  const handleImport = () => {
    triggerFileInput((file) => {
      importFromFile(file, () => loadPickings());
    });
  };

  const importExportProps = useMemo(() => buildInventaireImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    importLabel: t('common.import'),
    exportLabel: t('common.export'),
    generatingLabel: p('generating'),
    isGeneratingPDF,
    handlers: {
      onImport: handleImport,
      onExportCSV: handleExport,
      onExportPDF: handleExportPDF,
      onExportExcel: handleExportExcel,
    },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, t, p]);

  return (
    <>
    <InventoryConfigNotice
      message={!inventoryConfig.enableWarehouses ? p('notice_warehouses_disabled_banner', { defaultValue: 'إدارة المستودعات معطّلة — فعّلها من إعدادات المخزون ← المستودعات.' }) : null}
    />
    <InventaireListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Package}
      statEntries={inventaireStatEntries}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      onCreate={openNewPickingModal}
      createLabel={p('new_picking')}
      importExport={importExportProps}
      filterActions={buildInventaireBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, p })}
    >
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <InventaireTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onSelectAll={handleSelectAll} p={p} />
                  
                  {isColVisible('picking_number') && (

                  
                    <TableColumnHeaderFilter columnKey="picking_number" label={p('col_picking_number')} value={colFilters.picking_number ?? ''} onChange={(v) => setColFilter('picking_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('customer_name') && (

                    <TableColumnHeaderFilter columnKey="customer_name" label={p('col_customer_order')} value={colFilters.customer_name ?? ''} onChange={(v) => setColFilter('customer_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('priority') && (

                    <TableColumnHeaderFilter columnKey="priority" label={p('col_priority')} value={colFilters.priority ?? ''} onChange={(v) => setColFilter('priority', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('progress_percentage') && (

                    <TableColumnHeaderFilter columnKey="progress_percentage" label={p('col_progress')} value={colFilters.progress_percentage ?? ''} onChange={(v) => setColFilter('progress_percentage', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('picker_name') && (

                    <TableColumnHeaderFilter columnKey="picker_name" label={p('col_picker')} value={colFilters.picker_name ?? ''} onChange={(v) => setColFilter('picker_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (

                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={INVENTAIRE_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {visiblePickings.length > 0 ? (
                  visiblePickings.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <InventaireTableRowCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      
                      {isColVisible('picking_number') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold flex items-center gap-2">
                            <ShoppingCart size={16} className="text-blue-400" />
                            {record.picking_number}
                          </span>
                          {record.picking_method && <span className="text-white/50 text-xs mt-0.5">{p('method_label')}: {record.picking_method}</span>}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('customer_name') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.customer_name || p('na')}</span>
                          {record.order_number && <span className="text-white/50 text-xs mt-0.5">{p('order_prefix')}: {record.order_number}</span>}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('priority') && (

                      
                        <td className="px-6 py-4">{getPriorityBadge(record.priority)}</td>

                      
                      )}
                      
                      {isColVisible('progress_percentage') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-white/70 text-sm">{record.picked_items || 0}/{record.total_items || 0}</span>
                            <span className="text-white font-semibold text-sm">{record.progress_percentage || 0}%</span>
                          </div>
                          {getProgressBar(record.progress_percentage)}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('picker_name') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.picker_name || p('unassigned')}</span>
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('status') && (

                      
                        <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div></td>

                      
                      )}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><ShoppingCart size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {visiblePickings.length > 0 ? (
              visiblePickings.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <ShoppingCart size={20} className="text-blue-400" />
                          {record.picking_number}
                        </h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                        {getPriorityBadge(record.priority)}
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_order')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_number')}:</span> {record.order_number || p('na')}</div>
                            <div><span className="font-medium">{p('label_customer')}:</span> {record.customer_name || p('na')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_progress')}</h5>
                          <div className="space-y-2 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_items')}:</span> {record.picked_items || 0}/{record.total_items || 0}</div>
                            <div className="w-full">{getProgressBar(record.progress_percentage)}</div>
                            <div className="text-white font-semibold">{record.progress_percentage || 0}%</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_assignment')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_picker')}:</span> {record.picker_name || p('unassigned')}</div>
                            <div><span className="font-medium">{p('label_method')}:</span> {record.picking_method || p('na')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_dates')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.picking_date && <div><span className="font-medium">{p('label_picking_date')}:</span> {formatDate(record.picking_date)}</div>}
                            {record.due_date && <div><span className="font-medium">{p('label_due_date')}:</span> {formatDate(record.due_date)}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <p>{record.notes || p('no_notes')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <ShoppingCart size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
    </InventaireListPageShell>

      {/* View Modal */}
      {showViewModal && viewingRecord && (
        <InventaireViewModalPortal show={showViewModal} record={viewingRecord} onClose={() => setShowViewModal(false)} maxWidth="1100px">
            <div className="flex justify-between items-center mb-6 p-6 pb-0">
              <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
              <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 pt-2 flex-1">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <ShoppingCart size={18} className="text-blue-400" />
                    {p('section_identification')}
                  </h3>
                  <div className={INVENTAIRE_FORM_GRID}>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_picking_number')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.picking_number}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_order_number')}</p>
                      <p className="text-white font-medium">{viewingRecord.order_number || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                      <div className="flex"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_priority')}</p>
                      <div className="flex">{getPriorityBadge(viewingRecord.priority)}</div>
                    </div>
                  </div>
                </div>

                <div className={INVENTAIRE_FORM_GRID}>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <User size={18} className="text-green-400" />
                      {p('section_customer_order')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_customer_field')}</p>
                        <p className="text-white font-medium">{viewingRecord.customer_name || p('na')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_method')}</p>
                        <p className="text-white font-medium">{viewingRecord.picking_method || p('na')}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Gauge size={18} className="text-purple-400" />
                      {p('section_progress')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_items')}</p>
                        <p className="text-white font-medium">{viewingRecord.picked_items || 0} / {viewingRecord.total_items || 0}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-2">{p('label_advancement')}</p>
                        {getProgressBar(viewingRecord.progress_percentage)}
                        <p className="text-white font-semibold mt-2">{viewingRecord.progress_percentage || 0}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText size={18} className="text-yellow-400" />
                    {p('section_notes')}
                  </h3>
                  <p className="text-white/80">{viewingRecord.notes || p('no_notes')}</p>
                </div>
            </div>
        </InventaireViewModalPortal>
      )}

      {/* Edit/Create Modal */}
      {showModal && (
        <InventaireModalPortal show={showModal} onClose={resetForm} maxWidth="1200px">
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('edit_preparation') : p('new_preparation_modal')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="picking-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <Check size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            
            <FacturationFormModalTabs
              tabs={pickingFormTabs}
              activeTab={pickingFormTab}
              onTabChange={setPickingFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('edit_preparation') : p('new_preparation_modal')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="picking-form" onSubmit={handleSubmit}>
              {pickingFormTab === 'basicInfo' && (
              <div className={INVENTAIRE_FORM_GRID}><div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_priority')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.priority}
                              onChange={(value) => setFormData({ ...formData, priority: value })}
                              options={[
                                { value: 'urgent', label: p('priority_urgent') },
                                { value: 'high', label: p('priority_high') },
                                { value: 'normal', label: p('priority_normal') },
                                { value: 'low', label: p('priority_low') }
                              ]}
                              placeholder={p('select_priority')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={statusOptions.map(s => ({ value: s.value, label: s.label, icon: <s.icon size={16} /> }))}
                              placeholder={p('select_status')}
                            />
                          </div>
                          {!editingRecord && (
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                                {p('select_customer_order')} *
                              </label>
                              <CustomSelect
                                value={selectedOrderId || ''}
                                onChange={(value) => handleOrderSelection(value)}
                                options={availableOrders.map(order => ({
                                  value: order.id.toString(),
                                  label: `${order.order_number} - ${order.customer_name}${order.total_amount ? ` (${formatBrandMoney(order.total_amount)})` : ''}`
                                }))}
                                placeholder={p('select_order_placeholder')}
                                disabled={loadingOrders}
                                searchable={true}
                              />
                              {loadingOrders && (
                                <p className="text-white/50 text-xs mt-1">{p('loading_orders')}</p>
                              )}
                            </div>
                          )}
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('picking_method')}</label>
                            <CustomSelect
                              value={formData.picking_method}
                              onChange={(value) => setFormData({ ...formData, picking_method: value })}
                              options={[
                                { value: 'order', label: p('method_order') },
                                { value: 'batch', label: p('method_batch') },
                                { value: 'zone', label: p('method_zone') },
                                { value: 'wave', label: p('method_wave') }
                              ]}
                              placeholder={p('method_placeholder')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                              {p('total_items')} {!editingRecord && selectedOrderId && <span className="text-blue-400 text-xs">{p('auto_calculated')}</span>}
                            </label>
                            {!editingRecord && selectedOrderId ? (
                              <input
                                type="text"
                                value={formData.total_items}
                                disabled
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white/70 cursor-not-allowed"
                              />
                            ) : (
                              <CustomNumberInput
                                value={formData.total_items}
                                onChange={(value) => setFormData({ ...formData, total_items: value })}
                                placeholder={p('total_items_placeholder')}
                                step={1}
                                min={0}
                              />
                            )}
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('picked_items')}</label>
                            <CustomNumberInput
                              value={formData.picked_items}
                              onChange={(value) => setFormData({ ...formData, picked_items: value })}
                              placeholder={p('picked_items_placeholder')}
                              step={1}
                              min={0}
                              max={parseInt(formData.total_items) || undefined}
                            />
                          </div></div>
              )}

              {pickingFormTab === 'assignmentInfo' && (
              <div className={INVENTAIRE_FORM_GRID}><div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('picker_id')}</label>
                            <CustomNumberInput
                              value={formData.assigned_to}
                              onChange={(value) => setFormData({ ...formData, assigned_to: value })}
                              placeholder={p('picker_id_placeholder')}
                              step={1}
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('picker_name')}</label>
                            <input type="text" value={formData.picker_name} onChange={(e) => setFormData({ ...formData, picker_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('picker_name_placeholder')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('warehouse')}</label>
                            <CustomSelect
                              value={formData.warehouse_id}
                              onChange={(value) => setFormData({ ...formData, warehouse_id: value })}
                              options={availableWarehouses.map(warehouse => ({
                                value: warehouse.id.toString(),
                                label: `${warehouse.name} (${warehouse.code || p('na')})`
                              }))}
                              placeholder={p('select_warehouse_placeholder')}
                              disabled={loadingWarehouses}
                              searchable={true}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('picking_date')}</label>
                            <CustomDatePicker
                              value={formData.picking_date}
                              onChange={(date) => setFormData({ ...formData, picking_date: date })}
                              placeholder={p('picking_date_placeholder')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('due_date')}</label>
                            <CustomDatePicker
                              value={formData.due_date}
                              onChange={(date) => setFormData({ ...formData, due_date: date })}
                              placeholder={p('due_date_placeholder')}
                              minDate={formData.picking_date}
                            />
                          </div>
                          <div className="flex items-center">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.verification_required}
                                onChange={(e) => setFormData({ ...formData, verification_required: e.target.checked })}
                                className="w-5 h-5 text-blue-600 bg-white/10 border-white/20 rounded focus:ring-blue-500 focus:ring-2"
                              />
                              <span className="ml-3 text-white font-medium">{p('verification_required')}</span>
                            </label>
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label>
                            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('notes_placeholder')} />
                          </div></div>
              )}
              </form>
            </div>
        </InventaireModalPortal>
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default PickingPreparationPage;

